import * as amplitude from '@amplitude/analytics-browser';
import { ChatbotRole, PostApiSpoChatbotBodyMessagesItem } from '@spo/types';
import { VpDeleteBinIcon, VpMinimizeIcon } from '@vtmn-play/icons/react';
import { VpIcon, VpIconButton, VpTextarea } from '@vtmn-play/react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatbot } from '../../hooks/mutations/useChatbot';
import { ChatbotMessages } from './ChatbotMessages';
import { Rating } from './Rating';
import './chatbot.scss';

export const Chatbot = () => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [userInput, setUserInput] = useState('');
  const [openChatbot, setOpenChatbot] = useState(false);
  const [openRating, setOpenRating] = useState(false);
  const [messages, setMessages] = useState<PostApiSpoChatbotBodyMessagesItem[]>(
    [],
  );
  const { t } = useTranslation();

  const mutationSuccess = (response: string) => {
    const updatedMessages = [
      ...messages,
      { role: ChatbotRole.AGENT, text: response },
    ];
    setMessages(updatedMessages);
  };

  const { mutate, isPending, isError } = useChatbot(messages, mutationSuccess);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessageRole = messages[messages.length - 1].role;
      if (lastMessageRole === ChatbotRole.USER) {
        mutate(messages);
      }
    }
  }, [messages]);

  const handleEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const userMsg = userInput.trim().replace(/(\r\n|\r|\n)/g, '\\n');
    if (!userMsg || isPending) {
      return;
    } else {
      event.stopPropagation();
      const updatedMessages = [
        ...messages,
        { role: ChatbotRole.USER, text: userMsg },
      ];
      setMessages(updatedMessages);
      setUserInput('');
    }
  };

  useEffect(() => {
    textAreaRef.current?.focus();
  }, [messages]);

  const endConversation = () => {
    setUserInput('');
    setMessages([]);
    setOpenChatbot(false);
    setOpenRating(false);
  };

  return (
    <div className="chatbot-container" data-testid="chatbot_container">
      <div className="chatbot-button">
        <VpIconButton
          size="medium"
          data-testid="chatbot_button"
          onClick={() => {
            if (!openChatbot) {
              amplitude.track('Open Chatbot');
            }
            setOpenChatbot(!openChatbot);
            setOpenRating(false);
          }}
        >
          <VpIcon name="customer-service" />
        </VpIconButton>
      </div>
      {openChatbot && (
        <div className="chatbot-window">
          <div className="chatbot-header" data-testid="chatbot_header">
            <div>
              <VpIcon name="user" />
              {t('chatbot.title')}
            </div>
            <div>
              <VpIconButton
                className="red-icon"
                size="small"
                variant="ghost"
                data-testid="minimize_chat-conversation"
                onClick={() => setOpenChatbot(false)}
              >
                <VpMinimizeIcon />
              </VpIconButton>
              <VpIconButton
                className="red-icon"
                size="small"
                variant="negative"
                data-testid="close_chat-conversation"
                onClick={() => setOpenRating(true)}
              >
                <VpDeleteBinIcon />
              </VpIconButton>
            </div>
          </div>
          {openRating ? (
            <Rating
              messages={messages}
              cancel={() => {
                setMessages([]);
                setOpenRating(false);
              }}
              endConversation={endConversation}
            />
          ) : (
            <ChatbotMessages
              messages={messages}
              isPending={isPending}
              isError={isError}
            />
          )}
          <div className="chatbot-input-area" data-testid="chatbot_footer">
            <VpTextarea
              value={userInput}
              ref={textAreaRef}
              placeholder={t('chatbot.placeholder')}
              data-testid="chatbot_textarea"
              onChange={({ currentTarget }) =>
                setUserInput(currentTarget.value)
              }
              onKeyUp={(event: KeyboardEvent<HTMLTextAreaElement>) =>
                event.key === 'Enter' && !event.shiftKey && handleEnter(event)
              }
              onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) =>
                event.stopPropagation()
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
